import { CongestionAvoidance } from "./congestion-avoidance_browser.js";
/**
 * TCP CUBIC algorithm.
 * @see {@link https://datatracker.ietf.org/doc/html/rfc8312}
 *
 * RTT time unit is milliseconds, following JavaScript convention.
 */
export class TcpCubic extends CongestionAvoidance {
    c;
    betaCubic;
    alphaAimd;
    t0 = 0;
    wMax = 0;
    wLastMax = 0;
    k = Number.NaN;
    ssthresh = Infinity;
    constructor({ iw = 2, c = 0.4, betaCubic = 0.7, } = {}) {
        super(iw);
        this.c = c;
        this.betaCubic = betaCubic;
        this.alphaAimd = 3 * (1 - betaCubic) / (1 + betaCubic);
    }
    increase(now, rtt) {
        if (now < this.t0 || rtt < 0.001) {
            // increase and decrease processed out-of-order, t would be negative, ignore
            // rtt is less than 1us, probably due to performance.now() inaccuracy, ignore
            return;
        }
        const { cwnd } = this;
        if (cwnd < this.ssthresh) { // slow start
            this.updateCwnd(cwnd + 1);
            return;
        }
        const t = now - this.t0; // milliseconds
        const wCubic = this.c * (t / 1000 - this.k) ** 3 + this.wMax;
        const wEst = this.wMax * this.betaCubic + this.alphaAimd * (t / rtt);
        if (wCubic < wEst) { // TCP friendly region
            this.updateCwnd(wEst);
            return;
        }
        // concave region or convex region
        // note: RFC8312 specifies `(W_cubic(t+RTT) - cwnd) / cwnd`, but NDN-DPDK benchmark shows
        //       that using `(W_cubic(t) - cwnd) / cwnd` increases throughput by 10%
        this.updateCwnd(cwnd + (wCubic - cwnd) / cwnd);
    }
    decrease(now) {
        this.t0 = now;
        let { cwnd } = this;
        if (cwnd < this.wLastMax) {
            this.wLastMax = cwnd;
            this.wMax = cwnd * (1 + this.betaCubic) / 2;
        }
        else {
            this.wMax = cwnd;
            this.wLastMax = cwnd;
        }
        this.k = Math.cbrt(this.wMax * (1 - this.betaCubic) / this.c);
        cwnd *= this.betaCubic;
        this.ssthresh = Math.max(cwnd, 2);
        this.updateCwnd(cwnd);
    }
}
